/**
 * Wrapper for functionality exposed through window.VCThreedConfigurator
 */

import { App } from "../../App";
import { IAppConfig, ISavedRingConfiguration } from "../../sharedInterfaces";
import { IMediaQueryConstants } from "../../styles/theme";
import { ICaptureConfig } from "../../Tools/Capture/CaptureConfig";
import { IAPIConfigurationInfo, IRingState } from "../RingStateController/RingStateController";

interface IVCThreedConfiguratorAPI {
  initialize:(config:IAppConfig, mount:HTMLDivElement, mediaQueryConstants:IMediaQueryConstants, onInitialized:()=>void)=>null,
  onResize:()=>void,
  onConfigurationChange:(configurationInfo:IAPIConfigurationInfo)=>void,
  getConfigurationInfo:()=>Promise<IAPIConfigurationInfo>,
  getCheckoutArgument:()=>Promise<IAPIConfigurationInfo>,
  saveRingConfiguration:()=>Promise<string>,
  getSavedRingConfiguration:()=>ISavedRingConfiguration,
  onOrderButtonClick:()=>void,
  getCaptureConfiguration:()=>ICaptureConfig,
  setCaptureConfiguration:(captureConfig:ICaptureConfig)=>Promise<void>,
  takeScreenshot:(download:boolean)=>Promise<Blob>
}

export class APIController {
  private _app:App;
  public windowObject:IVCThreedConfiguratorAPI;

  constructor( app:App ) {
    this._app = app;
  };// /constructor()

  public initialize = () => {
    this.windowObject = (window as any).VCThreeDConfigurator;
    this.windowObject.getConfigurationInfo = this.getConfigurationInfo;
    this.windowObject.getCheckoutArgument = this.getCheckoutArgument;
    this.windowObject.saveRingConfiguration = this._app.saveRingConfiguration;
    this.windowObject.getSavedRingConfiguration = this.getSavedRingConfiguration;
    this.windowObject.getCaptureConfiguration = this.getCaptureConfiguration;
    this.windowObject.setCaptureConfiguration = this.setCaptureConfiguration;
    this.windowObject.takeScreenshot = this.takeScreenshot;
  }

  public onConfigurationChange = (ringState:IRingState, price:number) => {
    const callback = this.windowObject.onConfigurationChange;
    if (!callback) return; //Abort if no onConfigurationChange-callback is set.

    const configurationInfo:IAPIConfigurationInfo = {
      ringState,
      price
    };

    callback(configurationInfo);
  }

  public getSavedRingConfiguration = ():ISavedRingConfiguration => {
    const ringState = this._app.state.ringStateController.getState();
    const fingerSizeFormat = this._app.state.fingerSizeFormat;
    const savedRingConfiguration = this._app.state.mainController.ringStateToSavedRingConfiguration(ringState, fingerSizeFormat);
    return savedRingConfiguration;
  }

  public getConfigurationInfo = async () => {
    const ringState = this._app.state.ringStateController.getState();
    const checkoutArgument = this._app.getCheckoutArgument();
    await this._app.updatePrice(checkoutArgument);
    const price = this._app.state.price;
    
    const configurationInfo:IAPIConfigurationInfo = {
      ringState,
      price
    };

    return configurationInfo;
  }

  public getCheckoutArgument = async () => {
    const ringState = this._app.state.ringStateController.getState();
    const checkoutArgument = this._app.getCheckoutArgument();
    await this._app.updatePrice(checkoutArgument);
    const price = this._app.state.price;

    const thumbnail = await this._app.state.renderEngine.getCheckoutScreenshot(false);
    checkoutArgument.thumbnail = thumbnail;

    const priceCheckConfig = this._app.state.requestService.generatePriceCheckConfig(checkoutArgument);
    const serializedCheckourArgument = priceCheckConfig.paramsSerializer(priceCheckConfig.params);

    const configurationInfo:IAPIConfigurationInfo = {
      ringState,
      checkoutArgument,
      serializedCheckourArgument,
      price
    };

    return configurationInfo;
  }

  public getCaptureConfiguration = ():ICaptureConfig => {
    return this._app.state.mainController.captureController.getConfiguration();
  }

  public setCaptureConfiguration = async (captureConfig:ICaptureConfig):Promise<void> => {
    return this._app.state.mainController.captureController.setConfiguration(captureConfig);
  }

  public takeScreenshot = async (download:boolean = false):Promise<Blob> => {
    return this._app.state.mainController.captureController.takeScreenshot(download);
  }

};// /class